import { useState, type FormEvent } from 'react';
import {
  useEntityHistory,
  useEntitySignals,
  useLinkOwnerTpin,
  useUnlinkOwnerTpin,
} from '../../api/hooks.js';
import { Button } from '../../components/Button.js';
import { Card, CardFoot } from '../../components/Card.js';
import { cx, LINK, MONO } from '../../components/classes.js';
import { DefRow } from '../../components/DefRow.js';
import { Field, TextInput } from '../../components/Field.js';
import { EmptyState, ProblemNote, SkeletonRows } from '../../components/Notes.js';
import { SignalLine } from '../../components/Rail.js';
import { STATUS_TEXT, StatusGlyph } from '../../components/StatusGlyph.js';
import { canIssue, useSessionStore } from '../../store/session.js';
import {
  companyTypeLabel,
  formatDate,
  formatInstant,
  humanise,
  plural,
  sourceLabel,
  titleCase,
} from '../../lib/format.js';
import type { EntityProfile, HistoryItem, LicenceItem, Signal } from '../../lib/types.js';
import { yesNo } from './summary.js';
import { Link } from 'react-router';

export type ProfileTab =
  | 'registry'
  | 'tax'
  | 'licences'
  | 'tenders'
  | 'screening'
  | 'group'
  | 'history'
  | 'signals';

const tone = (ok: boolean) =>
  ok ? 'font-semibold text-good' : 'font-semibold text-warn';

export function RegistryTab({ p }: { p: EntityProfile }) {
  const r = p.registry;
  if (!r) {
    return (
      <Card>
        <EmptyState title="No PACRA record is linked to this entity">
          The entity was created from another register. A PACRA record links here once the
          matcher — or a reviewer — joins one.
        </EmptyState>
      </Card>
    );
  }
  const pl = r.payload;
  const status = pl.entityStatus ?? '—';
  const active = status.trim().toLowerCase() === 'active';
  const earlier = Math.max(0, p.versions_count - 1);
  return (
    <Card>
      <DefRow first label="Entity number">
        <span className={MONO}>{pl.entityNumber ?? 'not served by PACRA'}</span>
      </DefRow>
      <DefRow label="Type">{companyTypeLabel(pl.companyType) ?? '—'}</DefRow>
      <DefRow label="Status">
        <span className={active ? 'font-semibold text-good' : 'font-semibold text-crit'}>
          {status}
        </span>
      </DefRow>
      <DefRow label="Registered">
        {pl.registrationDate ? formatDate(pl.registrationDate) : '—'}
      </DefRow>
      <DefRow label="Nature of business">
        {pl.natureOfBusiness ?? pl.isicDescription ?? '—'}
        {pl.isicCode ? (
          <span className={cx(MONO, 'ml-2 text-mute')}>ISIC {pl.isicCode}</span>
        ) : null}
      </DefRow>
      <DefRow label="Annual returns">
        <span className={tone(pl.annualReturnStatus === true)}>
          {pl.annualReturnStatus ? 'Current' : 'Not current'}
        </span>
        {pl.annualReturnMadeUpTo ? (
          <> — last made up to {formatDate(pl.annualReturnMadeUpTo)}</>
        ) : pl.annualReturnReason ? (
          <> — {pl.annualReturnReason}</>
        ) : null}
      </DefRow>
      <DefRow label="Beneficial owners">
        {pl.beneficialOwnerStatus === undefined
          ? '—'
          : pl.beneficialOwnerStatus
            ? 'Filed'
            : (pl.beneficialOwnerReason ?? 'Not filed')}
        <span className="ml-1 text-mute">(names not served by PACRA search)</span>
      </DefRow>
      <DefRow label="Nominal capital">
        {yesNo(pl.nominalCapitalStatus, pl.nominalCapitalReason)}
      </DefRow>
      <DefRow label="Par value">{yesNo(pl.parValueStatus, pl.parValueReason)}</DefRow>
      <CardFoot>
        Source: PACRA business register, read {formatInstant(r.provenance.fetched_at)}
        {earlier > 0 ? ` · ${plural(earlier, 'earlier version')} in History` : ''}
        {' · '}
        <a
          href={r.provenance.source_url}
          className={LINK}
          target="_blank"
          rel="noreferrer"
        >
          source
        </a>
      </CardFoot>
    </Card>
  );
}

export const TPIN_RE = /^\d{10}$/;

export const OWNER_TPIN_PROMPT =
  "Business names are taxed under the owner's personal TPIN. Enter the owner's TPIN to link the tax record.";

/**
 * The Tax tab (owner fix 2026-09-22). The Status row shows the server's
 * READING — the label coloured by the check, the meaning as a second line,
 * and the raw ZRA code in mono muted — never a raw enum as the fact. A
 * business name without a linked record gets the owner-TPIN form (secondary
 * button: the screen's one accent action stays "Generate verification
 * record" in the header); a linked owner record renders the owner's tax
 * facts with a personal-data note and an Unlink action (admin|verifier).
 */
export function TaxTab({ p }: { p: EntityProfile }) {
  const t = p.tax;
  if (t.owner_tpin_required) return <OwnerTpinForm p={p} />;
  if (!t.payload || !t.provenance) {
    return (
      <Card>
        <div className="flex items-center gap-3.5 px-6 pt-5">
          <StatusGlyph status={t.check} />
          <span className={cx('text-[15px] font-semibold', STATUS_TEXT[t.check])}>
            {t.status_label}
          </span>
        </div>
        <EmptyState title="No ZRA record is linked">
          Nothing on the ZRA taxpayer search matched this name at the linking threshold,
          and no TPIN joined it. Search by TPIN if you have one.
        </EmptyState>
      </Card>
    );
  }
  const pl = t.payload;
  const ownerLinked = t.basis === 'owner_tpin';
  return (
    <Card>
      {ownerLinked ? (
        <div className="flex items-center justify-between gap-4 border-b border-line bg-sunk px-6 py-3 text-[13px] text-mute">
          <span>
            Owner&rsquo;s personal TPIN ·{' '}
            <span className="font-semibold">personal data</span> — access is logged
          </span>
          <UnlinkAction entityId={p.id} />
        </div>
      ) : null}
      <DefRow first label="TPIN">
        <span className={MONO}>{pl.tpin ?? '—'}</span>
        {ownerLinked ? <span className="ml-2 text-mute">(the owner&rsquo;s)</span> : null}
      </DefRow>
      <DefRow label="Taxpayer name">
        {pl.taxPayerName ? titleCase(pl.taxPayerName) : '—'}
        {ownerLinked ? <span className="ml-2 text-mute">· personal data</span> : null}
      </DefRow>
      <DefRow label="Status">
        <span className="flex flex-col gap-0.5">
          <span className={cx('font-semibold', STATUS_TEXT[t.check])}>
            {t.status_label}
          </span>
          <span className="text-[13px] leading-snug text-mute">{t.status_meaning}</span>
          {t.status_raw ? (
            <span className={cx(MONO, 'text-[12px] text-mute')}>
              ZRA status code: {t.status_raw}
            </span>
          ) : null}
        </span>
      </DefRow>
      <DefRow label="Registered">
        {pl.effectiveDateOfReg ? formatDate(pl.effectiveDateOfReg) : '—'}
      </DefRow>
      <DefRow label="Tax types">
        {(pl.taxTypes ?? []).length ? (pl.taxTypes ?? []).join(' · ') : 'None listed'}
      </DefRow>
      <DefRow label={ownerLinked ? 'Link to owner' : 'Link to registry'}>
        {ownerLinked ? (
          'Owner’s TPIN, supplied and confirmed by a person'
        ) : t.link_confidence === null || t.link_confidence === 1 ? (
          'Joined by TPIN'
        ) : (
          <>
            Name match · confidence{' '}
            <span className={MONO}>{t.link_confidence.toFixed(2)}</span>
          </>
        )}
      </DefRow>
      <CardFoot>
        Source: ZRA taxpayer search, read {formatInstant(t.provenance.fetched_at)} ·{' '}
        <a
          href={t.provenance.source_url}
          className={LINK}
          target="_blank"
          rel="noreferrer"
        >
          source
        </a>
      </CardFoot>
    </Card>
  );
}

/** The unlinked business name: the prompt and the (secondary) link form. */
function OwnerTpinForm({ p }: { p: EntityProfile }) {
  const role = useSessionStore((s) => s.role);
  const link = useLinkOwnerTpin(p.id);
  const [tpin, setTpin] = useState('');
  const valid = TPIN_RE.test(tpin);
  const mayLink = canIssue(role);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (valid && !link.isPending) link.mutate({ tpin });
  };
  return (
    <Card>
      <div className="flex items-center gap-3.5 px-6 pt-5">
        <StatusGlyph status="attention" />
        <span className="text-[15px] font-semibold text-warn">{p.tax.status_label}</span>
      </div>
      <div className="px-6 pt-2 pb-1 text-[13px] leading-relaxed text-mute">
        {OWNER_TPIN_PROMPT} PACRA does not serve the owner&rsquo;s TPIN, so a person
        supplies it. The owner&rsquo;s record is personal data: it is flagged, and every
        access is logged.
      </div>
      {mayLink ? (
        <form onSubmit={submit} className="flex items-end gap-3 px-6 py-5">
          <div className="w-[220px]">
            <Field id="owner-tpin" label="Owner's TPIN">
              <TextInput
                id="owner-tpin"
                type="text"
                inputMode="numeric"
                maxLength={10}
                pattern="\d{10}"
                placeholder="10 digits"
                value={tpin}
                onChange={(e) => setTpin(e.target.value.replace(/\D/g, ''))}
                className={MONO}
              />
            </Field>
          </div>
          <Button type="submit" disabled={!valid || link.isPending}>
            {link.isPending ? 'Linking…' : 'Link tax record'}
          </Button>
        </form>
      ) : (
        <div className="px-6 py-5 text-[13px] text-mute">
          A verifier or admin can link the owner&rsquo;s TPIN; your role is read-only.
        </div>
      )}
      {link.isError ? (
        <div className="px-6 pb-5">
          <ProblemNote error={link.error} />
        </div>
      ) : null}
      <CardFoot>
        The trading name never appears in ZRA&rsquo;s taxpayer search — this is how
        business names work, not a finding.
      </CardFoot>
    </Card>
  );
}

function UnlinkAction({ entityId }: { entityId: string }) {
  const role = useSessionStore((s) => s.role);
  const unlink = useUnlinkOwnerTpin(entityId);
  if (!canIssue(role)) return null;
  return (
    <button
      type="button"
      onClick={() => unlink.mutate()}
      disabled={unlink.isPending}
      className={cx(LINK, 'cursor-pointer disabled:cursor-default disabled:opacity-60')}
    >
      {unlink.isPending ? 'Unlinking…' : 'Unlink'}
    </button>
  );
}

function licenceTitle(l: LicenceItem): string {
  const s = l.summary;
  const name = [s['premisesName'], s['name'], s['registrantName']].find(
    (v) => typeof v === 'string' && v,
  ) as string | undefined;
  return name ? titleCase(name) : `${sourceLabel(l.source)} record`;
}

function licenceDetail(l: LicenceItem): string {
  const s = l.summary;
  const bits = [
    s['permitNo'],
    s['licenceNumber'],
    s['code'],
    s['premisesType'],
    s['kind'],
    s['category'],
    s['grade'],
    s['status'],
    s['district'],
  ].filter((v): v is string => typeof v === 'string' && v.length > 0);
  return bits.join(' · ');
}

export function LicencesTab({ p }: { p: EntityProfile }) {
  if (p.licences.length === 0) {
    return (
      <Card>
        <EmptyState title="No licence records linked">
          None of the sector registers Dira mirrors (ZAMRA, NCC, HPCZ, ZICTA, PIA, SEC,
          ZICA, Bank of Zambia, the mining cadastre) lists this entity by name.
        </EmptyState>
      </Card>
    );
  }
  return (
    <Card>
      {p.licences.map((l, i) => (
        <div
          key={`${l.source}:${l.key}`}
          className={cx(
            'flex items-center gap-3.5 px-6 py-4',
            i > 0 && 'border-t border-line',
          )}
        >
          <StatusGlyph status="pass" />
          <span className="flex min-w-0 grow flex-col gap-0.5">
            <span className="text-[15px] font-semibold">{licenceTitle(l)}</span>
            <span className={cx(MONO, 'text-[12px] text-mute')}>
              {sourceLabel(l.source)} · {l.key}
              {licenceDetail(l) ? ` · ${licenceDetail(l)}` : ''} · read{' '}
              {formatInstant(l.fetched_at)}
            </span>
          </span>
          <span className={cx(MONO, 'text-[13px] text-mute')}>
            {l.confidence.toFixed(2)}
          </span>
        </div>
      ))}
    </Card>
  );
}

export function TendersTab() {
  return (
    <Card>
      <EmptyState title="Tender history is not linked yet">
        ZPPA e-GP awards join the profile when the platform's tender mirror is connected
        (planner P6). Until then, check the entity's name on e-GP directly.
      </EmptyState>
    </Card>
  );
}

export function ScreeningTab({ p }: { p: EntityProfile }) {
  const { review_pending: pending, hard_key_hits: gazette } = p.screening;
  const clear = pending.length === 0 && gazette.length === 0;
  return (
    <Card>
      <div className="flex items-center gap-3.5 px-6 py-5">
        <StatusGlyph status={clear ? 'pass' : 'attention'} />
        <span className="flex flex-col gap-0.5">
          <span className="text-[15px] font-semibold">
            {clear ? 'Sanctions & debarment clear' : 'Screening needs a look'}
          </span>
          <span className="text-[12px] text-mute">
            UN · OFAC · UK · EU · World Bank · AfDB · Gazette — name screening; a score is
            never a match
          </span>
        </span>
      </div>
      {pending.map((h, i) => (
        <div
          key={`${h.source}:${h.candidate}:${i}`}
          className="grid grid-cols-[22px_minmax(0,1fr)_80px] items-center gap-3.5 border-t border-line px-6 py-3.5 text-[14px]"
        >
          <StatusGlyph status="attention" label="Review pending" />
          <span>
            <span className="font-semibold">{h.candidate}</span>
            <span className="text-mute">
              {' '}
              on {sourceLabel(h.source)} · read {formatInstant(h.fetched_at)}
            </span>
          </span>
          <span className={cx(MONO, 'text-right text-[13px]')}>{h.score.toFixed(2)}</span>
        </div>
      ))}
      {gazette.map((g, i) => (
        <div
          key={`${g.issue_no}:${i}`}
          className="grid grid-cols-[22px_minmax(0,1fr)] items-center gap-3.5 border-t border-line px-6 py-3.5 text-[14px]"
        >
          <StatusGlyph status="attention" label="Gazette notice" />
          <span>
            <span className="font-semibold">{humanise(g.notice_type)}</span>
            <span className="text-mute">
              {' '}
              · Gazette {g.issue_no} · {formatDate(g.issue_date)} · joined by PACRA number
            </span>
          </span>
        </div>
      ))}
      <CardFoot>
        Screening lists are read nightly; the Gazette is joined by the PACRA entity
        number, never by name alone.
      </CardFoot>
    </Card>
  );
}

export function GroupTab({ p }: { p: EntityProfile }) {
  if (p.group.length === 0) {
    return (
      <Card>
        <EmptyState title="No group relationships recorded">
          Group edges (same group, parent, trades as) are derived from the registers; none
          join this entity yet.
        </EmptyState>
      </Card>
    );
  }
  return (
    <Card>
      {p.group.map((e, i) => (
        <div
          key={e.id}
          className={cx(
            'grid grid-cols-[200px_minmax(0,1fr)_80px] items-center gap-4 px-6 py-3.5 text-[14px]',
            i > 0 && 'border-t border-line',
          )}
        >
          <span className="text-mute">
            {humanise(e.type)} · {e.direction === 'out' ? 'to' : 'from'}
          </span>
          <Link
            to={`/entities/${e.entity.id}`}
            className="font-semibold hover:text-accent"
          >
            {titleCase(e.entity.name)}
          </Link>
          <span className={cx(MONO, 'text-right text-[13px] text-mute')}>
            {e.confidence.toFixed(2)}
          </span>
        </div>
      ))}
      <CardFoot>Seen since {formatDate(p.group[0]!.first_seen_at)}.</CardFoot>
    </Card>
  );
}

export function historyLine(h: HistoryItem): string {
  if (h.kind === 'version')
    return `${sourceLabel(h.source)} record read (${h.source_key})`;
  const diff = h.diff[0];
  const change = diff && diff.after !== undefined ? ` → ${String(diff.after)}` : '';
  return `${humanise(h.type)}${change} · ${sourceLabel(h.source)}`;
}

export function HistoryTab({ p }: { p: EntityProfile }) {
  const history = useEntityHistory(p.id);
  if (history.isPending)
    return (
      <SkeletonRows rows={4} className="rounded-card border border-line bg-surface" />
    );
  if (history.isError) return <ProblemNote error={history.error} />;
  const items = history.data.data;
  if (items.length === 0) {
    return (
      <Card>
        <EmptyState title="No history yet">
          Every read of a source record is kept as a version; changes between versions
          become events.
        </EmptyState>
      </Card>
    );
  }
  return (
    <Card>
      {items.map((h, i) => (
        <div
          key={`${h.kind}:${h.id}`}
          className={cx(
            'grid grid-cols-[170px_minmax(0,1fr)_90px] items-center gap-4 px-6 py-3.5 text-[14px]',
            i > 0 && 'border-t border-line',
          )}
        >
          <span className={cx(MONO, 'text-[12px] text-mute')}>{formatInstant(h.at)}</span>
          <span>{historyLine(h)}</span>
          <span className="text-right text-[12px] text-mute">
            {h.kind === 'event' ? 'event' : 'version'}
          </span>
        </div>
      ))}
      <CardFoot>{plural(p.versions_count, 'version')} on file · newest first.</CardFoot>
    </Card>
  );
}

export function signalDetail(s: Signal): string {
  const bits = [`Since ${formatDate(s.opened_at)}`, `rule v${s.rule_version}`];
  const days = s.evidence['days'] ?? s.evidence['days_overdue'];
  if (typeof days === 'number') bits.push(`${days} days`);
  if (s.resolved_at) bits.push(`resolved ${formatDate(s.resolved_at)}`);
  return bits.join(' · ');
}

export function SignalsTab({ p }: { p: EntityProfile }) {
  const all = useEntitySignals(p.id);
  const items = all.data?.data ?? p.signals;
  if (all.isError) return <ProblemNote error={all.error} />;
  if (items.length === 0) {
    return (
      <Card>
        <EmptyState title="No signals">
          Rules run nightly over the mirror; when one fires on this entity it is listed
          here with its evidence.
        </EmptyState>
      </Card>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {items.map((s) => (
        <SignalLine
          key={s.id}
          severity={s.severity}
          title={humanise(s.type)}
          detail={signalDetail(s)}
        />
      ))}
    </div>
  );
}
